import { c as _c } from "react/compiler-runtime";
import { useAsyncEffect } from "ahooks";
import config from "app/libraryConfig";
import gsap, { ScrollTrigger } from "gsap/all";
import { isBrowser } from "library/deviceDetection";
import { sleep } from "library/functions";
import { ScreenContext } from "library/ScreenContext";
import { createScrollLock } from "library/Scroll";
import { type RefObject, use, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { instantScrollToAnchor } from "./util";

/**
 * if you're making a preloader, set this to a number of seconds to manually block the main thread for debugging
 *
 * this is incredibly useful because it makes it very easy to tell if your preloader
 * is gpu-accelerated (FAST) or bound to the main thread (SLOW)
 */
const DEBUG_BLOCK_THREAD_FOR_SECONDS = 0;

/**
 * force the preloader to remain in a certain state
 * useful for making the preloader
 */
const FORCE_PRELOADER_STATE = undefined as "loading" | "ready" | undefined;
const createDevKey = () => FORCE_PRELOADER_STATE ? Math.random() : 0;

// block the main thread for debugging
const useBlockThread = () => {
  if (isBrowser && DEBUG_BLOCK_THREAD_FOR_SECONDS > 0) {
    const start = performance.now();
    while (performance.now() - start < DEBUG_BLOCK_THREAD_FOR_SECONDS * 1000) {}
  }
};
const globalReadyPromises: Promise<unknown>[] = [];
const globalCompletePromises: Promise<unknown>[] = [];
let globalComplete = false;
const lock = createScrollLock("lock");
const setGlobalComplete = () => {
  if (!globalComplete) ScrollTrigger.refresh();
  lock.release();
  globalComplete = true;
};

/**
 * because multiple preloader hooks will:
 * - run at the same time
 * - need to wait for each other
 * - could run in any order
 *
 * we need to wait for promises recursively
 */
const recursiveAllSettled = async (promises: Promise<unknown>[], promisesToExclude: Promise<unknown>[] = []): Promise<void> => {
  const promisesCopy = [...promises].filter(promise => !promisesToExclude.includes(promise));
  if (promisesCopy.length === 0) return;
  await Promise.allSettled(promisesCopy);
  await recursiveAllSettled(promises, [...promisesToExclude, ...promisesCopy]);
};

/**
 * we only ever want to do this once!
 */
let hasProcessedScroll = false;
const processScroll = async () => {
  if (!isBrowser) return;
  if (hasProcessedScroll) return;

  // if there's an anchor in the URL, scroll to it instead of resetting scroll
  const hash = window.location.hash;
  if (hash && document.querySelector(hash)) {
    await instantScrollToAnchor(hash);
  } else if (window.scrollY < window.innerHeight) window.scrollTo(0, 0);
  hasProcessedScroll = true;
};
export const usePreloader = t0 => {
  const $ = _c(23);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    minDuration: t2,
    stopAnimations,
    stopNoWaitAnimations,
    slowAnimations,
    scope,
    customAnimation: t3
  } = t1;
  const minDuration = t2 === undefined ? 0 : t2;
  let t4;
  if ($[2] !== t3) {
    t4 = t3 === undefined ? {} : t3;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    beforeReady,
    beforeComplete
  } = t4;
  const {
    initComplete
  } = use(ScreenContext);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = globalComplete ? {
      ready: true,
      completed: true,
      isAtPageTop: true,
      devKey: createDevKey()
    } : {
      ready: false,
      completed: false,
      isAtPageTop: null,
      devKey: createDevKey()
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [output, setOutput] = useState(t5);
  useBlockThread();
  const [animationReadyPromise] = useState(_temp);
  const [animationCompletePromise] = useState(_temp2);
  let t6;
  let t7;
  if ($[5] !== animationCompletePromise || $[6] !== animationReadyPromise) {
    t6 = () => {
      if (config.overridePreloaderResolvers) {
        return;
      }
      globalReadyPromises.push(animationReadyPromise.promise);
      globalCompletePromises.push(animationCompletePromise.promise);
    };
    t7 = [animationReadyPromise, animationCompletePromise];
    $[5] = animationCompletePromise;
    $[6] = animationReadyPromise;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[9] !== animationCompletePromise || $[10] !== animationReadyPromise || $[11] !== beforeComplete || $[12] !== beforeReady || $[13] !== initComplete || $[14] !== minDuration || $[15] !== output.ready || $[16] !== scope || $[17] !== slowAnimations || $[18] !== stopAnimations || $[19] !== stopNoWaitAnimations) {
    t8 = async () => {
      if (!initComplete) {
        return;
      }
      if (output.ready) {
        return;
      }
      if (FORCE_PRELOADER_STATE === "loading") {
        return;
      }
      if (stopAnimations && !scope || slowAnimations && !scope) {
        throw new Error("scope is required in order to correctly stop animations");
      }
      await processScroll();
      if (beforeReady) {
        await Promise.allSettled([beforeReady]);
      }
      if (slowAnimations && scope) {
        const animatedElements = Array.from(scope.current?.querySelectorAll(slowAnimations) ?? []);
        for (const element of animatedElements) {
          const animations = element.getAnimations();
          for (const animation of animations) {
            globalReadyPromises.push(new Promise(resolve => {
              gsap.to(animation, {
                playbackRate: 0,
                duration: 1,
                onComplete: resolve
              });
            }));
          }
        }
      }
      if ((stopAnimations || stopNoWaitAnimations) && scope?.current) {
        const animatedElements_0 = Array.from(stopAnimations ? scope.current?.querySelectorAll(stopAnimations) : []).map(_temp3);
        const noWaitAnimatedElements = Array.from(stopNoWaitAnimations ? scope.current?.querySelectorAll(stopNoWaitAnimations) : []).map(_temp4);
        for (const {
          element: element_2,
          type
        } of [...animatedElements_0, ...noWaitAnimatedElements]) {
          const animations_0 = element_2.getAnimations();
          for (const animation_0 of animations_0) {
            const totalDuration = Number(animation_0.effect?.getTiming().duration);
            const currentTime = Number(animation_0.currentTime);
            const completedIterations = Math.floor(currentTime / totalDuration);
            animation_0.effect?.updateTiming({
              iterations: completedIterations + 1
            });
            if (type === "wait") {
              globalReadyPromises.push(animation_0.finished);
            }
          }
        }
      }
      animationReadyPromise.resolve(true);
      await recursiveAllSettled(globalReadyPromises);
      const timeSinceStart = performance.now();
      const timeToWait = Math.max(0, minDuration - timeSinceStart);
      await sleep(timeToWait);
      const beforeAnimations = document.body.getAnimations({
        subtree: true
      });
      flushSync(() => {
        setOutput(_temp5);
      });
      const afterAnimations = document.body.getAnimations({
        subtree: true
      });
      const newAnimations = afterAnimations.filter(a => !beforeAnimations.includes(a));
      for (const animation_1 of newAnimations) {
        globalCompletePromises.push(animation_1.finished);
      }
      if (beforeComplete) {
        globalCompletePromises.push(beforeComplete);
      }
      animationCompletePromise.resolve(true);
      await recursiveAllSettled(globalCompletePromises);
      setGlobalComplete();
      setOutput(_temp6);
      if (FORCE_PRELOADER_STATE === "ready") {
        setTimeout(() => setOutput({
          ready: false,
          completed: false,
          isAtPageTop: null,
          devKey: createDevKey()
        }), 1000);
      }
    };
    t9 = [initComplete, output.ready, minDuration, scope, slowAnimations, stopAnimations, stopNoWaitAnimations, beforeReady, beforeComplete, animationReadyPromise, animationCompletePromise];
    $[9] = animationCompletePromise;
    $[10] = animationReadyPromise;
    $[11] = beforeComplete;
    $[12] = beforeReady;
    $[13] = initComplete;
    $[14] = minDuration;
    $[15] = output.ready;
    $[16] = scope;
    $[17] = slowAnimations;
    $[18] = stopAnimations;
    $[19] = stopNoWaitAnimations;
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useAsyncEffect(t8, t9);
  if (FORCE_PRELOADER_STATE === "loading") {
    let t10;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        ready: false,
        completed: false,
        isAtPageTop: null,
        devKey: 0
      };
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    return t10;
  }
  return output;
};
function _temp() {
  return Promise.withResolvers();
}
function _temp2() {
  return Promise.withResolvers();
}
function _temp3(element_0) {
  return {
    element: element_0,
    type: "wait"
  };
}
function _temp4(element_1) {
  return {
    element: element_1,
    type: "noWait"
  };
}
function _temp5(p) {
  return {
    ready: true,
    completed: false,
    isAtPageTop: window.scrollY < window.innerHeight,
    devKey: p.devKey
  };
}
function _temp6(p_0) {
  return {
    ...p_0,
    completed: true
  };
}